import {
  MyElementOrText,
  MyNode,
} from 'lib-knowledge-document-structure/plateTypes';
import { Point } from 'lib-knowledge-document-structure/slate/package';
import { proxy, ref } from 'valtio';

import { Block } from './shortcut.blocks';
import { filterBlocks } from './shortcut.utils';

const HIDE_AFTER_INVALID_NUMBER = 3;

export type State = {
  search: string;
  // This is used to know when we can hide after X many empty results passed that last valid search
  lastValidSearch: string;
  selectedBlockIndex: number;
  filteredBlocks: ReadonlyArray<Block>;
  point: Point | null;
  newLine: boolean;
  oldNodeState: MyElementOrText | null;
  anchorElement: HTMLElement | null;
};

const initialState: State = {
  search: '',
  selectedBlockIndex: 0,
  filteredBlocks: [],
  lastValidSearch: '',
  newLine: false,
  point: null,
  oldNodeState: null,
  anchorElement: null,
};

export const shortcutState = proxy<State>(initialState);

function set(to: State = initialState): void {
  Object.keys(shortcutState).forEach((key) => {
    // eslint-disable-next-line
    // @ts-ignore looping in TS is annoying...
    shortcutState[key] = to[key];
  });
}

export const actions = {
  hide: set,
  trigger: set,
  show({
    point,
    oldNodeState,
    newLine,
    anchorElement,
  }: {
    point: Point;
    oldNodeState: MyNode;
    newLine: boolean;
    anchorElement?: HTMLElement;
  }): void {
    set({
      ...initialState,
      filteredBlocks: filterBlocks(''),
      newLine,
      point: point,
      anchorElement: anchorElement ? ref(anchorElement) : null,
      // eslint-disable-next-line
      // @ts-ignore
      oldNodeState: oldNodeState,
    });
  },
  setSearch(search: string): void {
    const filteredBlocks = filterBlocks(search);

    // If we are X characters past the last valid search, then hide.
    if (filteredBlocks.length === 0) {
      const lengthSinceLastValid =
        search.length - shortcutState.lastValidSearch.length;
      if (lengthSinceLastValid > HIDE_AFTER_INVALID_NUMBER) {
        set();
        return;
      }
    }

    shortcutState.selectedBlockIndex = 0; // Reset index to top
    shortcutState.search = search;
    shortcutState.lastValidSearch =
      filteredBlocks.length > 0 ? search : shortcutState.lastValidSearch;
    shortcutState.filteredBlocks = filteredBlocks;
  },
  moveSelectionUp(): void {
    const nextIndex =
      shortcutState.selectedBlockIndex <= 0
        ? shortcutState.filteredBlocks.length - 1
        : shortcutState.selectedBlockIndex - 1;

    shortcutState.selectedBlockIndex = nextIndex;
  },
  moveSelectionDown(): void {
    const prevIndex =
      shortcutState.selectedBlockIndex >=
      shortcutState.filteredBlocks.length - 1
        ? 0
        : shortcutState.selectedBlockIndex + 1;
    shortcutState.selectedBlockIndex = prevIndex;
  },
  setIndex(newIndex: number): void {
    shortcutState.selectedBlockIndex = newIndex;
  },
};
